import { useCallback } from 'react';
import { pick } from 'ramda';
import { dispatcher } from 'dispatcher';
import { useDispatch } from 'react-redux';
import { useAuthSelector, useUserSelector } from '@core/js/store/hooks';
import { userSetAttributesAction } from '@core/js/hooks/user/store/actions';
import { publicEventsAPIService } from '@core/js/services/publicEventsAPI.service';

import * as pas from '@core/js/hooks/auth/pas';
import * as helpers from '@core/js/hooks/auth/helpers';
import * as actions from '@core/js/hooks/auth/store/actions';
import { useGetLoginMode } from '@core/js/hooks/auth/hooks/useGetLoginMode';
import { useLoginRequest } from '@core/js/hooks/auth/hooks/useLoginRequest';
import { useValidateSession } from '@core/js/hooks/auth/hooks/useValidateSession';
import { useBeforeLoginAction } from '@core/js/hooks/auth/hooks/useBeforeLoginAction';

export const useInitialLoginAndSessionValidation = () => {
    const dispatch = useDispatch();
    const request = useLoginRequest();
    const getLoginMode = useGetLoginMode();
    const validateSession = useValidateSession();
    const beforeLoginAction = useBeforeLoginAction();
    const { role, isLoggedIn } = useUserSelector(pick(['role', 'isLoggedIn']));
    const { isLoginInProcess, isLogoutInProcess } = useAuthSelector(pick(['isLoginInProcess', 'isLogoutInProcess']));

    return useCallback(async (inputUserName, password, rememberMeLogin, afterRegisterLogin, loginMode, fitToPlay) => {
        if (isLoggedIn) {
            return;
        }

        if (role === 'admin') {
            return Promise.reject({ errorMessage: 'Admin can\'t login to portal' });
        }

        if (isLoginInProcess || isLogoutInProcess || !request) {
            return Promise.reject();
        }

        dispatch(actions.setIsLoginInProcess(true));
        beforeLoginAction();

        try {
            const { languageCode } = request;
            const userNameLoginMode = getLoginMode(inputUserName);
            const initialCredentials = {
                userName: inputUserName,
                password,
                loginMode: loginMode || userNameLoginMode,
                languageCode,
                rememberMeLogin
            };

            const pasLoginResponse = await pas.login(initialCredentials);

            const {
                userId,
                loginName,
                userName,
                sessionToken,
                needsTokenRequest
            } = helpers.parsePasLoginResponse(pasLoginResponse);
            const credentials = { ...initialCredentials, userName, loginName, userId };

            dispatch(userSetAttributesAction({
                loginName,
                userId,
                userName,
                password,
                rememberMeLogin
            }));

            if (!validateSession(pasLoginResponse, credentials, {
                afterRegisterLogin, isAfterLoginWithCredentials: true, isLoginToNativeAppEnabled: true, fitToPlay
            })) {
                dispatch(actions.setIsLoginInProcess(false));

                return Promise.reject(pasLoginResponse);
            }

            if (needsTokenRequest) {
                const tokenResponse = await pas.getTempToken();

                return { ...credentials, sessionToken: tokenResponse.data.sessionToken.sessionToken };
            }

            if (!sessionToken) {
                dispatch(actions.setIsLoginInProcess(false));
                dispatcher.dispatch('auth:credentials:login:failed', loginMode?.toLowerCase()); // @theme
                publicEventsAPIService.trigger('auth:generic_login:error', 'Login error');

                return Promise.reject(pasLoginResponse);
            }

            return { ...credentials, sessionToken };
        } catch (error) {
            dispatch(actions.setIsLoginInProcess(false));
            dispatcher.dispatch('auth:credentials:login:failed', loginMode?.toLowerCase()); // @theme
            publicEventsAPIService.trigger('auth:generic_login:error', error?.data?.playerMessage || 'Login error');

            throw error;
        }
    }, [
        isLoggedIn, role, request, isLoginInProcess, isLogoutInProcess, getLoginMode,
        dispatch, beforeLoginAction, validateSession
    ]);
};
